import { hybridApiBaseUrl } from './hybridFlags';

const CLOUD_API_BASE = 'https://api.gamestar.co.il';
const STORAGE_PREFIX = 'hybrid:optionalEndpointDisabledUntil:';
const DISABLE_TTL_MS = 30 * 24 * 60 * 60_000;

export const OPTIONAL_HYBRID_ENDPOINT_KEYS = {
  lookupByPhone: 'lookup-by-phone',
  callNumberState: 'call-number-state',
  customerHistory: 'customer-history',
} as const;

type OptionalEndpointKey = (typeof OPTIONAL_HYBRID_ENDPOINT_KEYS)[keyof typeof OPTIONAL_HYBRID_ENDPOINT_KEYS];

const memoryDisabledUntil = new Map<string, number>();

function normalizeBase(url: string): string {
  return String(url || '').trim().replace(/\/+$/, '');
}

/** כשהדפדפן פונה לענן (api.gamestar.co.il) — ה-endpoints החדשים עדיין לא שם. */
export function isCloudPrimaryHybridClient(): boolean {
  const configured = normalizeBase(hybridApiBaseUrl);
  if (!configured || configured === CLOUD_API_BASE) return true;
  if (typeof window === 'undefined') return false;
  try {
    const protocol = String(window.location.protocol || '').toLowerCase();
    const host = String(window.location.hostname || '').trim().toLowerCase();
    if (protocol !== 'https:') return false;
    if (!host || host === 'localhost' || host === '127.0.0.1') return false;
    if (/^10\./.test(host) || /^192\.168\./.test(host)) return false;
    if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(host)) return false;
    return configured.startsWith('http://');
  } catch {
    return false;
  }
}

function readDisabledUntil(key: OptionalEndpointKey): number {
  const mem = memoryDisabledUntil.get(key) || 0;
  if (mem > Date.now()) return mem;
  if (typeof window === 'undefined') return 0;
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    const parsed = Number(raw || 0);
    if (Number.isFinite(parsed) && parsed > Date.now()) {
      memoryDisabledUntil.set(key, parsed);
      return parsed;
    }
  } catch {
    // ignore
  }
  return 0;
}

export function markOptionalHybridEndpointDisabled(key: OptionalEndpointKey, ttlMs = DISABLE_TTL_MS): void {
  const until = Date.now() + ttlMs;
  memoryDisabledUntil.set(key, until);
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, String(until));
  } catch {
    // ignore
  }
}

export function shouldSkipOptionalHybridEndpoint(key: OptionalEndpointKey): boolean {
  if (readDisabledUntil(key) > Date.now()) return true;
  if (isCloudPrimaryHybridClient()) return true;
  return false;
}

export function noteOptionalHybridEndpoint404(key: OptionalEndpointKey): void {
  markOptionalHybridEndpointDisabled(key);
}
